import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import styled, { keyframes } from "styled-components";
import { describeError, logger } from "utils/logger";

export const CAMERA_GRANTED_KEY = "cameraGrantedThisSession";

// --- Animations & Styles ---

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
`;

const PermissionContainer = styled.div`
  height: 100vh;
  width: 100%;
  padding: 16px;
  background: linear-gradient(135deg, #fdfcfb 0%, #e2d1c3 100%);
  display: flex;
  justify-content: center;
  align-items: center;
`;

const ContentBox = styled.div`
  background: rgba(255, 255, 255, 0.85);
  backdrop-filter: blur(18px);
  border-radius: 24px;
  padding: 40px;
  max-width: 820px;
  width: 100%;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.15);
  animation: ${fadeIn} 0.6s ease;

  @media (max-width: 768px) {
    padding: 24px;
  }
`;

const CameraIcon = styled.div`
  text-align: center;
  margin-bottom: 20px;
  font-size: 70px;
  line-height: 1;
`;

const Title = styled.h1`
  color: #2e2e2e;
  font-size: 30px;
  font-weight: 700;
  margin-bottom: 12px;
  text-align: center;
`;

const Subtitle = styled.h2`
  color: #5a5a5a;
  font-size: 18px;
  margin-bottom: 28px;
  text-align: center;
  font-weight: 400;
`;

const NoticeSection = styled.div`
  background: #faf7f2;
  border-left: 4px solid #d97706;
  padding: 18px;
  margin-bottom: 24px;
  border-radius: 0 12px 12px 0;
`;

const NoticeTitle = styled.h3`
  color: #3d3d3d;
  font-size: 18px;
  font-weight: 600;
  margin-bottom: 12px;
`;

const List = styled.ul`
  color: #4a4a4a;
  line-height: 1.6;
  padding-left: 20px;
  font-size: 15px;
`;

const ListItem = styled.li`
  margin-bottom: 8px;
`;

const PrivacyText = styled.p`
  color: #5a5a5a;
  text-align: center;
  margin-bottom: 28px;
  font-size: 15px;
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: center;
`;

const AccessButton = styled.button`
  background: linear-gradient(135deg, #d97706, #a855f7);
  color: white;
  border: none;
  padding: 14px 36px;
  font-size: 16px;
  font-weight: 600;
  border-radius: 32px;
  cursor: pointer;
  transition: all 0.3s ease;
  letter-spacing: 0.3px;

  &:hover {
    transform: translateY(-3px) scale(1.02);
    box-shadow: 0 8px 20px rgba(168, 85, 247, 0.35);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const ErrorBox = styled.div`
  margin-top: 16px;
  background: #fff3f3;
  color: #a10000;
  border-left: 4px solid #ef4444;
  padding: 16px;
  border-radius: 0 12px 12px 0;
  text-align: center;
  font-size: 15px;
  animation: ${fadeIn} 0.4s ease;
`;

// --- Component Logic ---

const CameraPermissionPage = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [permissionStatus, setPermissionStatus] = useState<"checking" | "granted" | "prompt" | "denied">("checking");
    const [isRequesting, setIsRequesting] = useState(false);
    const isMounted = useRef(true);
    const hasNavigated = useRef(false);

    // Both the permission listener and the button can fire once access is granted;
    // only navigate once, and never update state after leaving the page.
    const goToScan = () => {
        if (hasNavigated.current) return;
        hasNavigated.current = true;
        navigate("/scan", { replace: true });
    };

    useEffect(() => {
        isMounted.current = true;
        let status: PermissionStatus | undefined;

        const checkPermission = async () => {
            try {
                if (!navigator.permissions || !navigator.permissions.query) {
                    setPermissionStatus("prompt");
                    return;
                }
                status = await navigator.permissions.query({ name: "camera" } as any);
                if (!isMounted.current) return;
                setPermissionStatus(status.state as any);

                if (status.state === "granted") {
                    goToScan();
                    return;
                }

                status.onchange = () => {
                    if (!isMounted.current) return;
                    setPermissionStatus(status.state as any);
                    if (status.state === "granted") goToScan();
                };
            } catch {
                if (isMounted.current) setPermissionStatus("prompt");
            }
        };

        checkPermission();

        return () => {
            isMounted.current = false;
            if (status) status.onchange = null;
        };
    }, []);

    const requestCameraAccess = async () => {
        setIsRequesting(true);
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ video: true });
            stream.getTracks().forEach(track => track.stop());
            // A one-time grant ("Allow this time", or Safari's "Ask") leaves the
            // Permissions API at "prompt"; ProtectedScanRoute reads this flag so it
            // doesn't send the user straight back here.
            sessionStorage.setItem(CAMERA_GRANTED_KEY, "1");
            logger.info("camera_access_granted");
            goToScan();
        } catch (error: any) {
            sessionStorage.removeItem(CAMERA_GRANTED_KEY);
            logger.error("camera_access_failed", { error: describeError(error) });
            if (!isMounted.current) return;
            if (error.name === "NotAllowedError" || error.name === "SecurityError") {
                setPermissionStatus("denied");
            } else {
                setPermissionStatus("prompt");
            }
        } finally {
            if (isMounted.current) setIsRequesting(false);
        }
    };

    // --- Render ---

    return (
        <PermissionContainer>
            <ContentBox>
                <CameraIcon>📸</CameraIcon>

                {permissionStatus === "checking" && (
                    <>
                        <Title>Checking Camera Permissions…</Title>
                        <Subtitle>Please wait while we verify your access.</Subtitle>
                    </>
                )}

                {permissionStatus === "prompt" && (
                    <>
                        <Title>Camera Access Required</Title>
                        <Subtitle>
                            To continue with ZakHealth scanning, we need access to your camera.
                        </Subtitle>

                        <NoticeSection>
                            <NoticeTitle>How we use your camera:</NoticeTitle>
                            <List>
                                <ListItem>
                                    Camera access is used solely to scan your face for identity verification.
                                </ListItem>
                                <ListItem>
                                    No photos or videos are recorded without your explicit consent.
                                </ListItem>
                                <ListItem>
                                    We do not store or share your video feed with third parties.
                                </ListItem>
                                <ListItem>
                                    Access ends once the verification process is complete.
                                </ListItem>
                            </List>
                        </NoticeSection>

                        <PrivacyText>
                            Your privacy and data security are our top priority. By allowing camera
                            access, you help us ensure a safe and trusted experience.
                        </PrivacyText>

                        <ButtonContainer>
                            <AccessButton onClick={requestCameraAccess} disabled={isRequesting}>
                                {isRequesting ? "Requesting Access..." : "Allow Camera Access"}
                            </AccessButton>
                        </ButtonContainer>
                    </>
                )}

                {permissionStatus === "denied" && (
                    <>
                        <Title>Camera Access Blocked</Title>
                        <Subtitle>
                            You’ve denied camera access. Please enable it in your browser settings.
                        </Subtitle>

                        <ErrorBox>
                            <p>
                                To continue, go to your browser’s <strong>Site Settings → Camera</strong> and allow access for this page.
                            </p>
                            <p style={{ marginTop: "8px", fontSize: "14px" }}>
                                After enabling, refresh this page to proceed.
                            </p>
                        </ErrorBox>
                    </>
                )}
            </ContentBox>
        </PermissionContainer>
    );
};

export default CameraPermissionPage;
